import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, MoreVertical } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3"><span className="w-7 h-7 shrink-0 rounded-full bg-[#2C4A3E] text-white text-sm grid place-items-center">{n}</span>
    <span className="text-sm text-stone-700 pt-1">{children} {Icon && <Icon className="inline w-4 h-4 text-[#2C4A3E]" />}</span></li>
);

export const InstallApp = ({ className = "" }) => {
  const [installed, setInstalled] = useState(standalone());
  const [, setTick] = useState(0);
  const [help, setHelp] = useState(false);
  useEffect(() => {
    const t = () => setTick((x) => x + 1);
    const done = () => { setInstalled(true); window.__installPrompt = null; };
    window.addEventListener("cl-installable", t);
    window.addEventListener("appinstalled", done);
    return () => { window.removeEventListener("cl-installable", t); window.removeEventListener("appinstalled", done); };
  }, []);
  if (installed) return null;
  const install = async () => {
    const p = window.__installPrompt;
    if (!p) return setHelp(true);
    p.prompt();
    const { outcome } = await p.userChoice;
    window.__installPrompt = null;
    if (outcome === "accepted") setInstalled(true);
  };
  const ios = isIOS();
  return (
    <>
      <button onClick={install} data-testid="install-app-btn" className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-[#2C4A3E]/20 text-[#2C4A3E] text-sm hover:bg-[#F1EADB] transition-colors whitespace-nowrap ${className}`}>
        <Download className="w-4 h-4" /> <span className="hidden sm:inline">Install app</span>
      </button>
      <Dialog open={help} onOpenChange={setHelp}>
        <DialogContent className="bg-[#FDFBF7] max-w-md" data-testid="install-help-dialog">
          <div className="flex items-center gap-4">
            <img src="/icon-192.png" alt="ConsciousLearn app icon" className="w-16 h-16 rounded-2xl" />
            <DialogTitle className="font-display text-2xl text-[#2C4A3E]">Install ConsciousLearn</DialogTitle>
          </div>
          <p className="text-sm text-stone-600">Get the app on your home screen. It opens full screen, like any other app. No app store needed.</p>
          <ol className="space-y-3 mt-2" data-testid={ios ? "install-steps-ios" : "install-steps-other"}>
            {ios ? (<>
              <Step n={1} icon={Share}>Open this site in Safari and tap the Share button</Step>
              <Step n={2} icon={PlusSquare}>Scroll and tap "Add to Home Screen"</Step>
              <Step n={3}>Tap "Add". The ConsciousLearn icon appears on your home screen.</Step>
            </>) : (<>
              <Step n={1} icon={MoreVertical}>Open your browser menu (Chrome, Edge or Samsung Internet)</Step>
              <Step n={2}>Tap "Install app" or "Add to Home screen"</Step>
              <Step n={3}>Confirm. ConsciousLearn opens from your home screen or desktop.</Step>
            </>)}
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
};
